import { Flex } from '@chakra-ui/react';

import { ProviderPlanId } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import { BentoLayoutSize } from 'common/bento/lib/layout';
import useLayoutSizeQuery from 'common/bento/lib/layout/useLayoutSizeQuery';
import { formatAmount } from 'common/formatAmount';
import {
  INSURANCE_SUMMARY_PLUS_URL,
  INSURANCE_SUMMARY_PRO_BUSINESS_URL,
} from 'common/insurance';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import OBDocumentView from 'features/Bento/components/OBDocumentView';

import locales from './locales';

interface TermsAndConditionsProps {
  isSavingChoice: boolean;
  onAgreementPress: () => void;
  plan: {
    providerPlanId: ProviderPlanId;
    brandName: string;
  };
  shouldSeeProAndBusinessInsurance: boolean;
}

/**
 * A page where the user can approve the insurance terms and conditions.
 */
const TermsAndConditions = asBentoPage<TermsAndConditionsProps>(
  ({
    isSavingChoice,
    onAgreementPress,
    onPrev,
    plan,
    shouldSeeProAndBusinessInsurance,
  }) => {
    const isLarge = useLayoutSizeQuery(BentoLayoutSize.lg);

    // used to display the warning about price raise for business
    // to remove when new price plan are released
    const isLegacyBusiness = plan.providerPlanId === 'moral_person_business';

    return (
      <OBDocumentView
        data-testid="terms-and-conditions"
        onClose={onPrev}
        src={
          shouldSeeProAndBusinessInsurance
            ? INSURANCE_SUMMARY_PRO_BUSINESS_URL
            : INSURANCE_SUMMARY_PLUS_URL
        }
      >
        <Heading marginBottom="space-32" size="lg">
          {locales.formatString(locales.title, plan.brandName)}
        </Heading>

        {!isLarge && isLegacyBusiness ? null : (
          <Text marginBottom="space-32" variant="primary">
            {locales.formatString(locales.text, plan.brandName)}
          </Text>
        )}

        {isLegacyBusiness ? (
          <Callout marginBottom="space-32" variant="warning">
            <Callout.Content>{locales.warningTitle}</Callout.Content>

            <Callout.Content>
              {locales.formatString(
                locales.warningText,
                formatAmount({
                  forcePlusSign: false,
                  value: 29,
                  withNoMinorUnitDigits: true,
                }),
              )}
            </Callout.Content>
          </Callout>
        ) : null}
        <Flex justifyContent="flex-end">
          <Button isLoading={isSavingChoice} onClick={onAgreementPress}>
            {locales.formatString(locales.agree, plan.brandName)}
          </Button>
        </Flex>
      </OBDocumentView>
    );
  },
);

export default TermsAndConditions;
